import { Radio, TextInput } from "@inceptionbg/ui-components";
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../../../AppContent";
import { IInputComponentProps, Input } from "../../../../Elements/Input";
import { updateContactInfo } from "../../../../repos/ContactRepo";
import { IFarmNew } from "../../../../types/IFarm";
import { IContactInfoNew, IContactType } from "../../../../types/IContactInfo";
import { inputPattern } from "../../../../utils/InputPatternValidation";
import { ContactConfirmationDialog } from "../../../Dialogs/ContactConfirmationDialog";
import { SubSection } from "../../../SubSection";
import { Form } from "../../../Wrapper/FormWrapper";
import { stripPhonePrefix } from "../../../../utils/StringUtils";
import { ENV } from "../../../../App";

interface Props {
  setFarm?: Dispatch<SetStateAction<IFarmNew>>;
}

export const ContactInfo: FC<Props> = ({ setFarm }) => {
  const [formData, setFormData] = useState<IContactInfoNew>({
    deliveryType: "INBOX",
  });
  const [edit, setEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState<IContactType | "">(
    ""
  );

  const { t } = useTranslation();
  const { farm, reloadFarm, isFarmInApproval } = useContext(FarmContext);
  const isProd = ENV === "PROD";
  const isUserProcessingFarm = !!farm?.registrationApproval?.userProcessing;
  const disableFarmAdministrativeProcedureEdit =
    !!farm?.registrationApproval?.administrativeProcedure &&
    farm?.registrationApproval?.approvalStatus !== "WAITING_COMPLEMENT";

  useEffect(() => {
    if (farm && !setFarm) {
      setFormData({
        email: farm.contactInfo?.email,
        phoneNumber: stripPhonePrefix(farm.contactInfo?.phoneNumber),
        deliveryType: farm.contactInfo.deliveryType,
      });
    }
  }, [farm, setFarm]);

  useEffect(() => {
    !!setFarm && setFarm((e) => ({ ...e, contactInfo: formData }));
  }, [formData, setFarm]);

  const contactInfo: IInputComponentProps[] = [
    {
      label: t("Email"),
      labelHelper: t("EmailHelper"),
      value: farm?.contactInfo?.email,
      input: (
        <TextInput
          value={formData.email}
          setValue={(e) => setFormData({ ...formData, email: e })}
          required
          inputProps={{ type: "email" }}
          error={
            !setFarm &&
            (!formData.email ||
              (!isProd &&
                !!farm?.contactInfo?.email &&
                !farm?.contactInfo?.emailVerified))
          }
        />
      ),
      alert: isProd
        ? {
            text: !formData.email ? t("RequiredFieldError") : undefined,
            type: !formData.email ? "error" : "success",
            active: !setFarm && !formData.email,
          }
        : {
            text: !formData.email
              ? t("RequiredFieldError")
              : !farm?.contactInfo?.emailVerified
              ? t("EmailNotVerified")
              : farm?.contactInfo?.emailVerified
              ? t("EmailVerified")
              : undefined,
            type:
              !formData.email || !farm?.contactInfo?.emailVerified
                ? "error"
                : "success",
            active:
              !setFarm &&
              (!formData.email ||
                (!!farm?.contactInfo?.email &&
                  farm?.contactInfo?.emailVerified !== undefined)),
            action:
              !!farm?.contactInfo?.email && !farm?.contactInfo?.emailVerified
                ? {
                    label: t("VerifyEmail"),
                    onClick: () => setConfirmDialogType("Email"),
                  }
                : undefined,
          },
    },
    {
      label: t("PhoneNumber"),
      labelHelper: t("PhoneNumberHelper"),
      value: farm?.contactInfo?.phoneNumber,
      input: (
        <div className="flex align-center mb-3">
          <p className="mr-2">+381</p>
          <TextInput
            required
            placeholder="64123456"
            value={formData.phoneNumber}
            setValue={(e) => setFormData({ ...formData, phoneNumber: e })}
            inputProps={{ pattern: inputPattern.phone }}
            error={
              !setFarm &&
              (!formData.phoneNumber ||
                (!isProd &&
                  !!farm?.contactInfo?.phoneNumber &&
                  !farm?.contactInfo?.phoneNumberVerified))
            }
          />
        </div>
      ),
      alert: isProd
        ? {
            text: !formData.phoneNumber ? t("RequiredFieldError") : undefined,
            type: !formData.phoneNumber ? "error" : "success",
            active: !setFarm && !formData.phoneNumber,
          }
        : {
            text: !formData.phoneNumber
              ? t("RequiredFieldError")
              : !!farm?.contactInfo?.phoneNumber &&
                !farm?.contactInfo?.phoneNumberVerified
              ? t("PhoneNumberNotVerified")
              : !!farm?.contactInfo?.phoneNumber &&
                farm?.contactInfo?.phoneNumberVerified
              ? t("PhoneNumberVerified")
              : undefined,
            type:
              !formData.phoneNumber ||
              (!!farm?.contactInfo?.phoneNumber &&
                !farm?.contactInfo?.phoneNumberVerified)
                ? "error"
                : "success",
            active:
              !setFarm &&
              (!formData.phoneNumber ||
                (!!farm?.contactInfo?.phoneNumber &&
                  farm?.contactInfo?.phoneNumberVerified !== undefined)),
            action:
              !!farm?.contactInfo?.phoneNumber &&
              !farm?.contactInfo?.phoneNumberVerified
                ? {
                    label: t("VerifyPhoneNumber"),
                    onClick: () => setConfirmDialogType("Phone"),
                  }
                : undefined,
          },
    },
    {
      label: t("PreferedDeliveryType"),
      value: t(`ContactDeliveryType${formData?.deliveryType}`),
      hidden: isProd,
      input: (
        <div className="flex column">
          <Radio
            className="disabled-text"
            label={t("ContactDeliveryTypeINBOX")}
            value="INBOX"
            // selected={formData?.deliveryType!}
            selected={"INBOX"}
            disabled={true}
            // setSelected={(e) => setFormData({ ...formData, deliveryType: e })}
            setSelected={() => {}}
          />
          <Radio
            label={t("ContactDeliveryTypeEMAIL")}
            value="EMAIL"
            selected={formData?.deliveryType!}
            setSelected={(e) => setFormData({ ...formData, deliveryType: e })}
          />
          <Radio
            label={t("ContactDeliveryTypeSMS")}
            value="SMS"
            selected={formData?.deliveryType!}
            setSelected={(e) => setFormData({ ...formData, deliveryType: e })}
          />
        </div>
      ),
    },
  ];

  const onSubmit = () => {
    if (farm?.uuid) {
      setIsLoading(true);
      updateContactInfo(farm.uuid, {
        ...formData,
        phoneNumber: `+381${formData.phoneNumber}`,
      })
        .then(() => {
          reloadFarm(() => {
            setIsLoading(false);
          });
        })
        .catch(() => setIsLoading(false));
    }
  };

  return (
    <SubSection
      title={t("ContactTitleOrdered")}
      expandable={!setFarm}
      alerts={
        setFarm
          ? []
          : [
              {
                text: t("RequiredFieldsMissing"),
                type: "error",
                hidden: !!formData?.phoneNumber && !!formData?.email,
              },
              ...(!isProd
                ? [
                    {
                      text: t("EmailNotVerifiedInfo"),
                      type: "error" as const,
                      hidden:
                        !farm?.contactInfo?.email ||
                        !!farm?.contactInfo?.emailVerified,
                    },
                    {
                      text: t("PhoneNumberNotVerifiedInfo"),
                      type: "error" as const,
                      hidden:
                        !farm?.contactInfo?.phoneNumber ||
                        !!farm?.contactInfo?.phoneNumberVerified,
                    },
                  ]
                : []),
            ]
      }
    >
      {setFarm ? (
        <div className="form-container">
          {contactInfo.map(
            (e) => !e.hidden && <Input key={e.label} component={e} edit />
          )}
        </div>
      ) : (
        <>
          <Form
            data={contactInfo}
            edit={edit}
            setEdit={setEdit}
            submitButton={{
              editLabel: t(edit ? "Confirm" : "ContactEdit"),
              onSubmit,
              hidden:
                (isFarmInApproval && !isUserProcessingFarm) ||
                disableFarmAdministrativeProcedureEdit,
            }}
            onResetForm={() =>
              setFormData({
                ...farm?.contactInfo,
                deliveryType: farm?.contactInfo?.deliveryType || "INBOX",
                phoneNumber: stripPhonePrefix(farm?.contactInfo?.phoneNumber),
              })
            }
            isLoading={isLoading}
          />
          {!isProd && !!confirmDialogType && (
            <ContactConfirmationDialog
              isOpen={true}
              onClose={() => setConfirmDialogType("")}
              type={confirmDialogType}
            />
          )}
        </>
      )}
    </SubSection>
  );
};
